import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import CodeInput from './CodeInput';
import PlatformMessagesBanner from './PlatformMessagesBanner';
import { api } from '../services/api';
import '../styles/Signup.css';
import {
  getHostKind,
  getOrigins,
  getReturnToParam,
} from '../utils/host';
import { isMicrosoftOAuthEnabled } from '../utils/env';
import finbaseLogo from '../assets/logos/finbase-logo.svg';

const ENABLE_MICROSOFT_OAUTH = isMicrosoftOAuthEnabled();
const TERMS_URL = 'https://finbaseco.com/terms';
const PRIVACY_URL = 'https://finbaseco.com/privacy';

const getApiErrorMessage = (err, fallback) => (
  err.response?.data?.message || err.response?.data?.error || fallback
);

const Signup = () => {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState(new URLSearchParams(window.location.search).get('email') || '');
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get('code') || '');
  const [signupCompletionToken, setSignupCompletionToken] = useState('');
  const [fullName, setFullName] = useState(new URLSearchParams(window.location.search).get('full_name') || '');
  const [orgType, setOrgType] = useState('type-ind');
  const [orgName, setOrgName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [inviteCode, setInviteCode] = useState(() => {
    const raw = new URLSearchParams(window.location.search).get('invite_code');
    return raw && raw !== 'undefined' ? raw : '';
  });
  const [inviteOrgName, setInviteOrgName] = useState('');
  const [inviteEmailLocked, setInviteEmailLocked] = useState(false);
  const [isOAuth, setIsOAuth] = useState(false);
  const [error, setError] = useState('');
  const [showResendAlert, setShowResendAlert] = useState(false); // New state for alert
  const [showInviteDeclineConfirm, setShowInviteDeclineConfirm] = useState(false);
  const [inviteAuthOptions, setInviteAuthOptions] = useState([]);
  const [inviteAuthLoading, setInviteAuthLoading] = useState(false);
  const [showInviteAcceptOptions, setShowInviteAcceptOptions] = useState(false);
  const [inviteAuthMethod, setInviteAuthMethod] = useState('');
  const [inviteOtpCode, setInviteOtpCode] = useState('');
  const [inviteOtpToken, setInviteOtpToken] = useState('');
  const [inviteOtpStatus, setInviteOtpStatus] = useState('');
  const [inviteOtpLoading, setInviteOtpLoading] = useState(false);
  const [inviteOtpActive, setInviteOtpActive] = useState(false);
  const [inviteOtpResendCooldown, setInviteOtpResendCooldown] = useState(0);
  const [signupResendCooldown, setSignupResendCooldown] = useState(0);
  const [signupShake, setSignupShake] = useState(false);
  const [inviteOtpShake, setInviteOtpShake] = useState(false);
  const [needsOrgSetup, setNeedsOrgSetup] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [existingUserNoOrg, setExistingUserNoOrg] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const { signupComplete } = React.useContext(AuthContext);
  const history = useHistory();
  const isSubmitting = useRef(false);
  const hostKind = getHostKind();
  const { idOrigin } = getOrigins();
  const returnTo = 'account';

  const redirectAfterAuth = (accessToken, refreshToken, sessionMetadata = {}) => {
    if (sessionMetadata?.redirect_url) {
      window.location.replace(sessionMetadata.redirect_url);
      return;
    }
    if (!accessToken || !refreshToken) return;
    if (hostKind === 'id') return;
    signupComplete(accessToken, refreshToken, sessionMetadata);
    history.push('/account');
  };

  const redirectToMfa = (payload) => {
    const url = new URL('/login', window.location.origin);
    if (returnTo) url.searchParams.set('return_to', returnTo);
    url.searchParams.set('email', email);
    url.searchParams.set('mfa', '1');
    url.searchParams.set('mfa_token', payload.mfa_token);
    url.searchParams.set('mfa_channel', payload.mfa_channel);
    if (payload.totp_setup_required) {
      url.searchParams.set('totp_setup_required', '1');
    }
    if (payload.phone_required) {
      url.searchParams.set('phone_required', '1');
    }
    if (payload.phone_masked) {
      url.searchParams.set('phone_masked', payload.phone_masked);
    }
    window.location.assign(url.toString());
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resume = params.get('resume');
    const hasInvite = params.get('invite_code');
    const nameParam = params.get('full_name');
    const oauthResume = params.get('oauth') === '1';
    if (nameParam && !fullName) setFullName(nameParam);
    if (resume && email) {
      setEmailVerified(true);
      setInviteEmailLocked(true);
      setExistingUserNoOrg(true);
      if (oauthResume) {
        setIsOAuth(true);
      }
      if (hasInvite && !oauthResume) {
        setStep('invite');
      } else {
        setNeedsOrgSetup(true);
        setStep('complete');
      }
      return;
    }
    if (email && code) {
      handleOAuthVerify();
    }
  }, []);


  useEffect(() => {
    const loadInviteDetails = async () => {
      const params = new URLSearchParams(window.location.search);
      const oauthResume = params.get('oauth') === '1';
      if (!inviteCode || inviteCode === 'undefined' || code || oauthResume) return;
      const resume = params.get('resume');
      const inviteEmailParam = params.get('email');
      if (!inviteEmailParam) {
        setError('Invite link is missing the email address.');
        return;
      }
      try {
        const response = await api.get(`/acct-mgmt/v1/signup/invite?invite_code=${inviteCode}&email=${encodeURIComponent(inviteEmailParam)}`);
        setInviteOrgName(response.data.org_name || '');
        if (response.data.email) {
          setEmail(response.data.email);
          setInviteEmailLocked(true);
        }
        if (resume) {
          setEmailVerified(true);
          setExistingUserNoOrg(true);
        }
        setStep('invite');
      } catch (err) {
        setError(getApiErrorMessage(err, 'Invite code is invalid or expired'));
        setStep('email');
      }
    };
    loadInviteDetails();
  }, [inviteCode]);

  useEffect(() => {
    const loadInviteAuthOptions = async () => {
      if (!inviteCode || !email || inviteCode === 'undefined') return;
      setInviteAuthLoading(true);
      try {
        const response = await api.post('/acct-mgmt/v1/auth/options', {
          email,
          invite_code: inviteCode,
          flow: 'invite',
          return_to: returnTo,
        });
        const options = (response.data?.options || []).filter((option) => ENABLE_MICROSOFT_OAUTH || option.code !== 'microsoft');
        setInviteAuthOptions(options);
      } catch (err) {
        setError(getApiErrorMessage(err, 'Failed to load sign-in options'));
      } finally {
        setInviteAuthLoading(false);
      }
    };
    loadInviteAuthOptions();
  }, [inviteCode, email]);

  const handleInviteDecline = async () => {
    if (!inviteCode) return;
    setError('');
    try {
      await api.post('/acct-mgmt/v1/account/invite/respond', { invite_code: inviteCode, accept: false });
      setInviteCode('');
      setInviteOrgName('');
      setInviteEmailLocked(false);
      if (needsOrgSetup || emailVerified) {
        setStep('complete');
      } else {
        setStep('email');
      }
      setError('Invite declined. You can create your own account.');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to decline invite'));
    }
  };

  const handleInviteAccept = async (methodOverride = 'password') => {
    setInviteAuthMethod(methodOverride);
    setStep('complete');
  };

  const getInviteOptions = () => inviteAuthOptions;

  const handleInviteAcceptClick = () => {
    const options = getInviteOptions();
    if (options.length === 1) {
      const option = options[0];
      if (option.type === 'password') {
        handleInviteAccept('password');
      } else if (option.type === 'otp') {
        handleInviteOtpRequest();
      } else if (option.url) {
        window.location.assign(option.url);
      }
      return;
    }
    setShowInviteAcceptOptions((prev) => !prev);
  };

  const handleInviteOtpRequest = async () => {
    setInviteOtpStatus('');
    setInviteOtpLoading(true);
    try {
      setInviteOtpCode('');
      await api.post('/acct-mgmt/v1/signup/otp/request', { email, invite_code: inviteCode });
      setInviteOtpActive(true);
      setInviteOtpStatus('sent');
      setInviteOtpResendCooldown(30);
    } catch (err) {
      setInviteOtpStatus(getApiErrorMessage(err, 'Failed to send one-time code.'));
    } finally {
      setInviteOtpLoading(false);
    }
  };

  useEffect(() => {
    if (inviteOtpResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setInviteOtpResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [inviteOtpResendCooldown]);

  useEffect(() => {
    if (signupResendCooldown <= 0) return;
    const timer = setInterval(() => {
      setSignupResendCooldown((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [signupResendCooldown]);

  const handleInviteOtpVerify = async () => {
    if (!inviteOtpCode) {
      setInviteOtpStatus('Enter the code we emailed to you.');
      return;
    }
    setInviteOtpLoading(true);
    try {
      const response = await api.post('/acct-mgmt/v1/signup/otp/verify', {
        email,
        invite_code: inviteCode,
        code: inviteOtpCode,
      });
      if (response.data?.otp_token) {
        setInviteOtpToken(response.data.otp_token);
        setInviteOtpStatus('');
        handleInviteAccept('otp_email');
      } else {
        setInviteOtpStatus('Failed to verify code.');
        setInviteOtpCode('');
        setInviteOtpShake(true);
        setTimeout(() => setInviteOtpShake(false), 500);
      }
    } catch (err) {
      setInviteOtpStatus(getApiErrorMessage(err, 'Failed to verify code.'));
      setInviteOtpCode('');
      setInviteOtpShake(true);
      setTimeout(() => setInviteOtpShake(false), 500);
    } finally {
      setInviteOtpLoading(false);
    }
  };

  const handleOAuthVerify = async () => {
    try {
      const response = await api.post('/acct-mgmt/v1/signup/verify', { email, code });
      if (response.data.error) {
        setError(response.data.error);
        setStep('email');
      } else {
        if (!response.data.signup_completion_token) {
          setError('Verification could not be completed. Please request a new code.');
          setStep('email');
          return;
        }
        setSignupCompletionToken(response.data.signup_completion_token);
        setCode('');
        setIsOAuth(response.data.is_oauth);
        if (response.data.has_org) {
          setError('Email already registered. Please log in.');
          setStep('email');
          return;
        }
        setExistingUserNoOrg(Boolean(response.data.has_account) && !response.data.has_org);
        if (response.data.invite_code) {
          setInviteCode(response.data.invite_code);
          setInviteOrgName(response.data.invite_org_name || '');
          setInviteEmailLocked(true);
          setEmailVerified(true);
          if (response.data.full_name) setFullName(response.data.full_name);
          if (response.data.is_oauth) {
            setStep('complete');
            return;
          }
          setStep('invite');
          return;
        }
        if (response.data.needs_org_setup) {
          setNeedsOrgSetup(true);
        }
        setEmailVerified(true);
        if (response.data.full_name) setFullName(response.data.full_name);
        setStep('complete');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Verification failed'));
      setStep('verify');
      setCode('');
      setSignupShake(true);
      setTimeout(() => setSignupShake(false), 500);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/signup/email', { email });
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setSignupResendCooldown(30);
        setStep('verify');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to send verification code'));
    }
  };

  const handleVerifySubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const response = await api.post('/acct-mgmt/v1/signup/verify', { email, code });
      if (response.data.error) {
        setError(response.data.error);
        setStep('verify');
        setCode('');
        setSignupShake(true);
        setTimeout(() => setSignupShake(false), 500);
      } else {
        if (!response.data.signup_completion_token) {
          setError('Verification could not be completed. Please request a new code.');
          setStep('email');
          return;
        }
        setSignupCompletionToken(response.data.signup_completion_token);
        setCode('');
        setIsOAuth(response.data.is_oauth);
        if (response.data.has_org) {
          setError('Email already registered. Please log in.');
          setStep('email');
          return;
        }
        setExistingUserNoOrg(Boolean(response.data.has_account) && !response.data.has_org);
        if (response.data.invite_code) {
          setInviteCode(response.data.invite_code);
          setInviteOrgName(response.data.invite_org_name || '');
          setInviteEmailLocked(true);
          setEmailVerified(true);
          if (response.data.full_name) setFullName(response.data.full_name);
          if (response.data.is_oauth) {
            setStep('complete');
            return;
          }
          setStep('invite');
          return;
        }
        if (response.data.needs_org_setup) {
          setNeedsOrgSetup(true);
        }
        setEmailVerified(true);
        if (response.data.full_name) setFullName(response.data.full_name);
        setStep('complete');
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Verification failed'));
      setStep('verify');
      setCode('');
      setSignupShake(true);
      setTimeout(() => setSignupShake(false), 500);
    }
  };

  const handleCompleteSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!legalAccepted) {
      setError('You must agree to the Terms of Service and acknowledge the Privacy Policy to continue.');
      return;
    }
    if (!isOAuth && !existingUserNoOrg && inviteAuthMethod !== 'otp_email' && password !== passwordConfirm) {
      setError('Passwords do not match');
      return;
    }
    try {
      const response = await api.post('/acct-mgmt/v1/signup/complete', {
        email,
        full_name: fullName,
        org_type: orgType,
        org_name: orgName,
        password,
        confirm_password: passwordConfirm,
        invite_code: inviteCode,
        is_oauth: isOAuth,
        auth_method: inviteCode ? (inviteAuthMethod || 'password') : undefined,
        otp_token: inviteOtpToken || undefined,
        signup_completion_token: signupCompletionToken || undefined,
        legal_accepted: legalAccepted,
        return_to: returnTo,
      });
      if (response.data.error) {
        setError(response.data.error);
      } else if (response.data?.mfa_required) {
        redirectToMfa(response.data);
      } else {
        redirectAfterAuth(response.data.access_token, response.data.refresh_token, response.data);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Signup completion failed'));
    }
  };

  const handleResendCode = async () => {
    setError(''); // Clear any existing error
    try {
      const response = await api.post('/acct-mgmt/v1/signup/resend', { email });
      if (response.data.error) {
        setError(response.data.error);
      } else {
        setShowResendAlert(true); // Show alert on success
        setSignupResendCooldown(30);
        setTimeout(() => setShowResendAlert(false), 5000); // Hide after 5 seconds
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to resend code'));
    }
  };

  return (
    <div className="auth-page">
      <PlatformMessagesBanner surface="id_portal" />
      {inviteCode && (
        <div className="invite-banner w-100 mb-3 text-center" style={{ maxWidth: '440px' }} role="alert">
          <div className="fw-semibold">You’ve been invited to join our Finbase account</div>
          <div className="small text-muted">Organization: {inviteOrgName || 'Organization'}</div>
        </div>
      )}
      <div className="card auth-card shadow-sm">
        <div className="auth-logo">
          <img src={finbaseLogo} alt="Finbase" />
        </div>
        {step === 'email' && (
          <>
            <h1 className="h4 fw-bold text-dark mb-4 text-center">Let's Get You Registered</h1>
            {error && <p className="text-danger text-center mb-3">{error}</p>}
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-3">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={inviteEmailLocked}
                />
                {inviteEmailLocked && (
                  <div className="form-text">This email is tied to your invite.</div>
                )}
              </div>
              <button type="submit" className="btn btn-primary w-100">Send Code</button>
            </form>
            <p className="mt-3 text-center text-muted">
              Already registered? <a href={`/login?return_to=${encodeURIComponent(returnTo)}`} className="text-primary">Sign in</a>
            </p>
            <div className="mt-4 text-center">
            <p className="text-muted mb-2 text-center">Or sign up with</p>
              <div className="d-flex flex-column gap-2">
                <a href={`${idOrigin}/api/id/v1/auth/google?return_to=${encodeURIComponent(returnTo)}`} className="btn btn-outline-secondary">
                  <i className="fab fa-google me-2"></i>Google
                </a>
                {ENABLE_MICROSOFT_OAUTH && (
                  <a href={`${idOrigin}/api/id/v1/auth/microsoft?return_to=${encodeURIComponent(returnTo)}`} className="btn btn-outline-secondary">
                    <i className="fab fa-microsoft me-2"></i>Microsoft
                  </a>
                )}
              </div>
            </div>
          </>
        )}
        {step === 'verify' && (
          <>
            <h1 className="h4 fw-bold text-dark mb-2 text-center">Verify your email</h1>
            <p className="text-muted mb-4 text-center">Please enter the code we sent to {email}</p>
            {error && <p className="text-danger text-center mb-3">{error}</p>}
            <form onSubmit={handleVerifySubmit}>
              <div className="mb-3 text-center">
                <label className="form-label text-center d-block">Verification Code</label>
                <CodeInput value={code} onChange={setCode} shake={signupShake} autoFocus />
              </div>
              <button type="submit" className="btn btn-primary w-100 mb-2" disabled={code.length < 6}>Verify</button>
              <button
                type="button"
                className="btn btn-outline-secondary w-100 mb-2"
                onClick={handleResendCode}
                disabled={signupResendCooldown > 0}
              >
                {signupResendCooldown > 0 ? `Resend Code (${signupResendCooldown}s)` : 'Resend Code'}
              </button>
              {showResendAlert && (
                <div className="alert alert-success mt-2" role="alert">
                  A new code has been sent, please check your inbox.
                </div>
              )}
            </form>
          </>
        )}
        {step === 'invite' && (
          <>
            <h1 className="h4 fw-bold text-dark mb-2 text-center">You’re invited</h1>
            <p className="text-muted mb-4 text-center">
              This invite is for <strong>{email}</strong>. Accept to complete registration, or decline if you weren’t expecting this invite.
            </p>
            {error && <p className="text-danger text-center mb-3">{error}</p>}
            {inviteAuthLoading && (
              <div className="text-muted mb-3 text-center">Loading sign-in options...</div>
            )}
            {!inviteAuthLoading && (
              <>
                <div className="d-grid gap-2 mt-3">
                  <button type="button" className="btn btn-primary" onClick={handleInviteAcceptClick}>
                    Accept Invite
                  </button>
                  {showInviteAcceptOptions && (
                    <div className="border rounded p-2 text-start">
                      <div className="text-muted small mb-2">Continue with</div>
                      <div className="d-flex flex-column gap-2">
                        {getInviteOptions().map((option) => {
                          const iconClass = option.code === 'google'
                            ? 'fab fa-google'
                            : option.code === 'microsoft'
                              ? 'fab fa-microsoft'
                              : option.code === 'apple'
                                ? 'fab fa-apple'
                                : option.code === 'atlassian'
                                  ? 'fab fa-atlassian'
                                  : null;
                          if (option.type === 'password') {
                            return (
                              <button key={option.code} type="button" className="btn btn-outline-secondary" onClick={() => handleInviteAccept('password')}>
                                {option.label}
                              </button>
                            );
                          }
                          if (option.type === 'otp') {
                            return (
                              <button
                                key={option.code}
                                type="button"
                                className="btn btn-outline-secondary"
                                onClick={handleInviteOtpRequest}
                                disabled={inviteOtpLoading}
                              >
                                {option.label}
                              </button>
                            );
                          }
                          return (
                            <a key={option.code} href={option.url} className="btn btn-outline-secondary">
                              {iconClass && <i className={`${iconClass} me-2`} aria-hidden="true"></i>}
                              {option.label}
                            </a>
                          );
                        })}
                      </div>
                      {inviteOtpActive && (
                        <div className="border rounded p-2 mt-3 text-center">
                          <label className="form-label small text-center d-block">Enter the Code We Emailed to You</label>
                        <div className="mb-2 text-center">
                          <CodeInput value={inviteOtpCode} onChange={setInviteOtpCode} disabled={inviteOtpLoading} shake={inviteOtpShake} autoFocus />
                        </div>
                          <div className="d-flex gap-2 justify-content-center">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={handleInviteOtpVerify}
                              disabled={inviteOtpLoading || inviteOtpCode.length < 6}
                            >
                              Verify Code
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={handleInviteOtpRequest}
                              disabled={inviteOtpLoading || inviteOtpResendCooldown > 0}
                            >
                              {inviteOtpResendCooldown > 0 ? `Resend Code (${inviteOtpResendCooldown}s)` : 'Resend Code'}
                            </button>
                          </div>
                          {inviteOtpStatus === 'sent' && (
                            <div className="text-success small mt-2">A new code has been sent.</div>
                          )}
                          {inviteOtpStatus && inviteOtpStatus !== 'sent' && (
                            <div className="text-danger small mt-2">{inviteOtpStatus}</div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="border-top pt-3 mt-3 d-grid gap-2">
                  <button type="button" className="btn btn-outline-danger" onClick={() => setShowInviteDeclineConfirm(true)}>
                    Decline Invite
                  </button>
                </div>
              </>
            )}
          </>
        )}
        {step === 'complete' && (
          <>
            <h1 className="h3 fw-bold text-dark mb-2 text-center">Complete your signup</h1>
            <p className="text-muted mb-4 text-center">Step 3: Finalize your account details</p>
            {error && <p className="text-danger text-center mb-3">{error}</p>}
            <form onSubmit={handleCompleteSubmit}>
              <div className="mb-3">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  readOnly
                />
              </div>
              <div className="mb-3">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  className="form-control"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </div>
              {!inviteCode && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Account Type</label>
                    <select
                      className="form-select"
                      value={orgType}
                      onChange={(e) => setOrgType(e.target.value)}
                    >
                      <option value="type-ind">Individual</option>
                      <option value="type-org">Organization</option>
                    </select>
                  </div>
                  {orgType === 'type-org' && (
                    <div className="mb-3">
                      <label className="form-label">Organization Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        required
                      />
                    </div>
                  )}
                </>
              )}
              {!isOAuth && !existingUserNoOrg && inviteAuthMethod !== 'otp_email' && (
                <div className="mb-3">
                  <label className="form-label">
                    Password
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <div className="form-text">8+ characters, 1 uppercase, 1 special character.</div>
                  <label className="form-label mt-2">Confirm Password</label>
                  <input
                    type="password"
                    className="form-control mt-2"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                  />
                </div>
              )}
              {inviteCode && !isOAuth && !inviteAuthMethod && (
                <div className="mb-3">
                  <div className="text-muted small mb-2">Or, complete registration with:</div>
                  <div className="d-flex flex-column gap-2">
                    <a href={`${idOrigin}/api/id/v1/auth/google?invite_code=${inviteCode}&email=${encodeURIComponent(email)}&return_to=${encodeURIComponent(returnTo)}`} className="btn btn-outline-secondary">
                      <i className="fab fa-google me-2"></i>Google
                    </a>
                    {ENABLE_MICROSOFT_OAUTH && (
                      <a href={`${idOrigin}/api/id/v1/auth/microsoft?invite_code=${inviteCode}&email=${encodeURIComponent(email)}&return_to=${encodeURIComponent(returnTo)}`} className="btn btn-outline-secondary">
                        <i className="fab fa-microsoft me-2"></i>Microsoft
                      </a>
                    )}
                  </div>
                </div>
              )}
              <div className="mb-3">
                <div className="form-check d-flex align-items-start gap-2">
                  <input
                    id="legalAccepted"
                    type="checkbox"
                    className="form-check-input"
                    checked={legalAccepted}
                    onChange={(e) => setLegalAccepted(e.target.checked)}
                    required
                  />
                  <label className="form-check-label mb-0" htmlFor="legalAccepted">
                    By completing registration, you agree to the <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms of Service</a> and acknowledge the <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>.
                  </label>
                </div>
              </div>
              <button type="submit" className="btn btn-primary w-100">Complete</button>
            </form>
          </>
        )}
        <div className="auth-footer">Finbase Single Sign-On: one account for all Finbase apps.</div>
        {showInviteDeclineConfirm && (
          <>
            <div className="modal-backdrop show"></div>
            <div className="modal d-block" tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Decline invite?</h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setShowInviteDeclineConfirm(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <p className="mb-0">
                      Declining will remove this invite. You can still create your own account.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setShowInviteDeclineConfirm(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={async () => {
                        setShowInviteDeclineConfirm(false);
                        await handleInviteDecline();
                      }}
                    >
                      Yes, Decline
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Signup;
